/**
 * EntityContext - Unified Entity Data Access Layer
 *
 * This context provides a single source of truth for entity data across the application.
 * It handles two resolution methods:
 *
 * 1. SUBDOMAIN RESOLUTION (Public pages)
 *    - Detects subdomain from URL (e.g., acmecorp.hrportal.com)
 *    - Used for public job listings and application pages
 *
 * 2. USER-BASED RESOLUTION (Admin pages)
 *    - Fetches entity based on authenticated user's entity_id
 *    - Used for admin dashboard, settings, and management pages
 *
 * Usage:
 *   const { entity, features, theme, isLoading } = useEntity();
 *   const isEnabled = useEntityFeature('customThemes');
 *   const { primaryColor, logoUrl } = useEntityTheme();
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Entity, FeatureFlags, User } from '../types';
import { supabase } from '../lib/supabaseClient';
import { useSubdomain } from '../hooks/useSubdomain';
import { getAuthUser } from '../services/authService';
import { switchActiveEntity } from '../services/userEntityService';

// ============================================================================
// Types
// ============================================================================

interface EntityTheme {
  primaryColor: string;
  secondaryColor: string;
  logoUrl?: string;
}

interface EntityContextState {
  // Core entity data
  entity: Entity | null;

  // Derived data for convenience
  features: FeatureFlags;
  theme: EntityTheme;

  // Loading and error states
  isLoading: boolean;
  error: string | null;

  // Resolution method info
  resolutionMethod: 'subdomain' | 'user' | 'none';

  // User info (when resolved by user)
  currentUser: User | null;
  isSuperAdmin: boolean;

  // Multi-entity support
  userEntities: Array<{ id: string; name: string }>;
  hasMultipleEntities: boolean;

  // Methods
  refreshEntity: () => Promise<void>;
  setUserEntity: (entityId: string) => Promise<void>;
}

// Default feature flags (all disabled for safety)
const DEFAULT_FEATURES: FeatureFlags = {
  resumeScreening: false,
  aiScreening: false,
  analytics: false,
  mockInterviews: false,
  callInitiation: false,
  customThemes: false,
};

// Default theme
const DEFAULT_THEME: EntityTheme = {
  primaryColor: '#4A3AFF',
  secondaryColor: '#5C9DFF',
  logoUrl: undefined,
};

// ============================================================================
// Context
// ============================================================================

const EntityContext = createContext<EntityContextState | undefined>(undefined);

// ============================================================================
// Provider
// ============================================================================

interface EntityProviderProps {
  children: ReactNode;
  /**
   * Resolution mode:
   * - 'auto': Tries user-based first (if authenticated), then subdomain
   * - 'subdomain': Only uses subdomain resolution (for public pages)
   * - 'user': Only uses user-based resolution (for admin pages)
   */
  mode?: 'auto' | 'subdomain' | 'user';
}

export const EntityProvider: React.FC<EntityProviderProps> = ({
  children,
  mode = 'auto'
}) => {
  // State
  const [entity, setEntity] = useState<Entity | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolutionMethod, setResolutionMethod] = useState<'subdomain' | 'user' | 'none'>('none');
  const [userEntities, setUserEntities] = useState<Array<{ id: string; name: string }>>([]);

  // Subdomain detection
  const { subdomain, isMainDomain } = useSubdomain();

  // ============================================================================
  // Entity Fetching Methods
  // ============================================================================

  /**
   * Fetch entity by subdomain (for public pages)
   */
  const fetchEntityBySubdomain = useCallback(async (sub: string): Promise<Entity | null> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('entities')
        .select('id, name, subdomain, logo, theme, features, is_active')
        .eq('subdomain', sub)
        .eq('is_active', true)
        .single();

      if (fetchError || !data) {
        console.warn('[EntityContext] Entity not found for subdomain:', sub);
        return null;
      }

      return mapEntityFromDatabase(data);
    } catch (err) {
      console.error('[EntityContext] Error fetching entity by subdomain:', err);
      return null;
    }
  }, []);

  /**
   * Fetch entity by ID (for authenticated users)
   */
  const fetchEntityById = useCallback(async (entityId: string): Promise<Entity | null> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('entities')
        .select('id, name, subdomain, logo, theme, features, is_active')
        .eq('id', entityId)
        .single();

      if (fetchError || !data) {
        console.error('[EntityContext] Entity not found for ID:', entityId);
        return null;
      }

      return mapEntityFromDatabase(data);
    } catch (err) {
      console.error('[EntityContext] Error fetching entity by ID:', err);
      return null;
    }
  }, []);

  /**
   * Map database record to Entity interface
   */
  const mapEntityFromDatabase = (data: any): Entity => {
    return {
      id: data.id,
      name: data.name,
      subdomain: data.subdomain,
      logo: data.logo,
      theme: {
        primaryColor: data.theme?.primaryColor || DEFAULT_THEME.primaryColor,
        secondaryColor: data.theme?.secondaryColor || DEFAULT_THEME.secondaryColor,
        logoUrl: data.theme?.logoUrl || data.logo, // Fallback to logo column
        headerBackgroundColor: data.theme?.headerBackgroundColor || '#1976d2',
        headerTextColor: data.theme?.headerTextColor || '#ffffff',
      },
      features: {
        resumeScreening: data.features?.resumeScreening === true,
        aiScreening: data.features?.aiScreening === true,
        analytics: data.features?.analytics === true,
        mockInterviews: data.features?.mockInterviews === true,
        callInitiation: data.features?.callInitiation === true,
        customThemes: data.features?.customThemes === true,
      },
    };
  };

  /**
   * Check if user is super admin
   */
  const checkSuperAdmin = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const { data } = await supabase
        .from('users')
        .select('is_super_admin')
        .eq('id', userId)
        .single();

      return data?.is_super_admin === true;
    } catch {
      return false;
    }
  }, []);

  // ============================================================================
  // Main Resolution Logic
  // ============================================================================

  const resolveEntity = useCallback(async () => {
    console.log('[EntityContext] Resolving entity...', { mode, subdomain, isMainDomain });
    setIsLoading(true);
    setError(null);

    try {
      // STEP 1: Try user-based resolution first (if mode allows)
      if (mode === 'auto' || mode === 'user') {
        const user = await getAuthUser();

        if (user) {
          console.log('[EntityContext] Authenticated user found:', user.email);
          setCurrentUser(user);

          // Check super admin status
          const superAdmin = await checkSuperAdmin(user.id);
          setIsSuperAdmin(superAdmin);

          // Fetch user's entity assignments for multi-entity support
          try {
            const { data: ueData } = await supabase
              .from('user_entities')
              .select('entity_id, entities(name)')
              .eq('user_id', user.id);

            if (ueData && ueData.length > 0) {
              setUserEntities(ueData.map((ue: any) => ({
                id: ue.entity_id,
                name: (ue.entities as any)?.name || 'Unknown',
              })));
            } else if (user.entityId) {
              // Fallback: use the single entity_id from user record
              const fallbackEntity = await fetchEntityById(user.entityId);
              if (fallbackEntity) {
                setUserEntities([{ id: fallbackEntity.id, name: fallbackEntity.name }]);
              }
            }
          } catch {
            // user_entities table may not exist yet
            if (user.entityId) {
              const fallbackEntity = await fetchEntityById(user.entityId);
              if (fallbackEntity) {
                setUserEntities([{ id: fallbackEntity.id, name: fallbackEntity.name }]);
              }
            }
          }

          // Fetch user's entity
          if (user.entityId) {
            const userEntity = await fetchEntityById(user.entityId);

            if (userEntity) {
              console.log('[EntityContext] Resolved entity from user:', userEntity.name);
              setEntity(userEntity);
              setResolutionMethod('user');
              setIsLoading(false);
              return;
            }
          }
        }
      }

      // STEP 2: Fall back to subdomain resolution (if mode allows)
      if (mode === 'auto' || mode === 'subdomain') {
        if (!isMainDomain && subdomain) {
          console.log('[EntityContext] Trying subdomain resolution:', subdomain);
          const subdomainEntity = await fetchEntityBySubdomain(subdomain);

          if (subdomainEntity) {
            console.log('[EntityContext] Resolved entity from subdomain:', subdomainEntity.name);
            setEntity(subdomainEntity);
            setResolutionMethod('subdomain');
            setIsLoading(false);
            return;
          } else {
            setError('Entity not found for this subdomain');
          }
        }
      }

      // STEP 3: No entity resolved
      console.log('[EntityContext] No entity resolved');
      setEntity(null);
      setResolutionMethod('none');

    } catch (err) {
      console.error('[EntityContext] Error resolving entity:', err);
      setError('Failed to load entity data');
      setEntity(null);
    } finally {
      setIsLoading(false);
    }
  }, [mode, subdomain, isMainDomain, fetchEntityBySubdomain, fetchEntityById, checkSuperAdmin]);

  // ============================================================================
  // Public Methods
  // ============================================================================

  /**
   * Force refresh entity data
   */
  const refreshEntity = useCallback(async () => {
    await resolveEntity();
  }, [resolveEntity]);

  /**
   * Manually set entity by ID (useful for super admins or multi-entity users switching entities)
   * Persists the change to users.entity_id in the database so RLS picks up the new entity.
   */
  const setUserEntity = useCallback(async (entityId: string) => {
    setIsLoading(true);
    try {
      // Update the database first so RLS policies reflect the new entity
      await switchActiveEntity(entityId);

      const newEntity = await fetchEntityById(entityId);
      if (newEntity) {
        setEntity(newEntity);
        setResolutionMethod('user');
        // Update currentUser's entityId in state
        setCurrentUser(prev => prev ? { ...prev, entityId } : prev);
      }
    } catch (err) {
      console.error('[EntityContext] Error switching entity:', err);
    }
    setIsLoading(false);
  }, [fetchEntityById]);

  // ============================================================================
  // Effects
  // ============================================================================

  // Initial resolution
  useEffect(() => {
    resolveEntity();
  }, [resolveEntity]);

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[EntityContext] Auth state changed:', event);
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        // Re-resolve entity only on actual sign in/out, not on token refresh
        // TOKEN_REFRESHED happens on tab focus and doesn't change entity data
        resolveEntity();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [resolveEntity]);

  // ============================================================================
  // Derived Values
  // ============================================================================

  const features: FeatureFlags = entity?.features || DEFAULT_FEATURES;

  const theme: EntityTheme = entity ? {
    primaryColor: entity.features.customThemes
      ? (entity.theme.primaryColor || DEFAULT_THEME.primaryColor)
      : DEFAULT_THEME.primaryColor,
    secondaryColor: entity.features.customThemes
      ? (entity.theme.secondaryColor || DEFAULT_THEME.secondaryColor)
      : DEFAULT_THEME.secondaryColor,
    // Logo always shown (from SuperAdmin or custom theme)
    logoUrl: entity.features.customThemes && entity.theme.logoUrl
      ? entity.theme.logoUrl
      : entity.logo,
  } : DEFAULT_THEME;

  // ============================================================================
  // Context Value
  // ============================================================================

  const hasMultipleEntities = userEntities.length > 1;

  const value: EntityContextState = {
    entity,
    features,
    theme,
    isLoading,
    error,
    resolutionMethod,
    currentUser,
    isSuperAdmin,
    userEntities,
    hasMultipleEntities,
    refreshEntity,
    setUserEntity,
  };

  return (
    <EntityContext.Provider value={value}>
      {children}
    </EntityContext.Provider>
  );
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Main hook to access entity context
 */
export const useEntity = (): EntityContextState => {
  const context = useContext(EntityContext);
  if (context === undefined) {
    throw new Error('useEntity must be used within an EntityProvider');
  }
  return context;
};

/**
 * Hook to check if a specific feature is enabled
 */
export const useEntityFeature = (feature: keyof FeatureFlags): boolean => {
  const { features } = useEntity();
  return features[feature] === true;
};

/**
 * Hook to access entity theme
 */
export const useEntityTheme = (): EntityTheme => {
  const { theme } = useEntity();
  return theme;
};

/**
 * Hook to check if current user is super admin
 */
export const useIsSuperAdmin = (): boolean => {
  const { isSuperAdmin } = useEntity();
  return isSuperAdmin;
};

/**
 * Hook to get current user from entity context
 */
export const useCurrentUser = (): User | null => {
  const { currentUser } = useEntity();
  return currentUser;
};

// ============================================================================
// Export for backward compatibility
// ============================================================================

export { EntityContext };
export type { EntityContextState, EntityTheme };
